import type { PropsWithChildren } from "react";
import { createContext, useEffect, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import { clientCodeHeader } from "@entrilia/shared/api/clientApiContext";
import { logError } from "@entrilia/shared/logging";
import { connect } from "@entrilia/shared/services/signalRConnector";
import { sessionCookieExists } from "@entrilia/shared/utilities/cookies";
import adminApi, { resourceChangesHubUrl } from "../api/adminApi";
import type { ResourceChangesConnection } from "../services/resourceChangesConnection";
import { createResourceChangesConnection } from "../services/resourceChangesConnection";

type ContextValue = Pick<ResourceChangesConnection, "addListener">;

const ResourceChangesContext = createContext<ContextValue | undefined>(undefined);

const getResourceChangesSubscriptionToken = withErrorHandling(adminApi.getResourceChangesSubscriptionToken);

const getSubscriptionToken = async () => {
  const [token, error] = await getResourceChangesSubscriptionToken();
  if (error) {
    logError(error, "[ResourceChangesContext] getResourceChangesSubscriptionToken");
    return undefined;
  }

  return token;
};

const createConnection = () =>
  createResourceChangesConnection({
    connect: () => connect(resourceChangesHubUrl, clientCodeHeader()),
    getSubscriptionToken,
    isAuthenticated: () => Promise.resolve(sessionCookieExists()),
  });

export const ResourceChangesContextProvider = ({ children }: PropsWithChildren) => {
  const [connection] = useState(createConnection);

  useEffect(() => {
    connection.start();
    return connection.stop;
  }, [connection]);

  return <ResourceChangesContext value={connection}>{children}</ResourceChangesContext>;
};

export default ResourceChangesContext;
